/**
 * The local availability mirror.
 *
 * Availability is kept in sync, not fetched when a quote is built (AVAILABILITY_SYNC.md). This
 * module is the read side of that mirror: a set of day records plus — and this is the part that
 * matters — the range of dates the mirror actually holds.
 *
 * WHY THE WINDOW IS PART OF THE OBJECT
 *
 * A mirror that knows only its days cannot tell "this unit is booked" from "nobody has fetched
 * that far ahead". Those look identical to a caller and mean opposite things: one is a reason to
 * go to NOT_WON with a supply-gap reason, the other is a reason to extend the sync window.
 *
 * This is not hypothetical. On 2026-09-03 a simulation of this exact search reported "0 units
 * available for 90 nights from 1 November" and it was false — the calendar had been fetched only
 * to 28 January, the stay ran off the end, and every absent day scored as unavailable. The number
 * was confident, wrong, and would have been acted on.
 *
 * So the window is carried with the days, every lookup can say `outsideWindow`, and a day that is
 * missing INSIDE the window is reported as a gap rather than silently treated as free or as
 * booked. Both are unknown; neither is a verdict.
 */

const DAY_MS = 86_400_000

export function parseDate(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    throw new TypeError(`expected a YYYY-MM-DD date, got: ${iso}`)
  }
  const t = Date.parse(iso + 'T00:00:00Z')
  const d = new Date(t)
  if (Number.isNaN(t) || d.toISOString().slice(0, 10) !== iso) throw new TypeError(`not a real date: ${iso}`)
  return d
}

export const addDays = (iso, n) => new Date(parseDate(iso).getTime() + n * DAY_MS).toISOString().slice(0, 10)

/** Every date from `from` to `to`, inclusive. */
export function datesBetween(from, to) {
  const end = parseDate(to).getTime()
  const out = []
  for (let t = parseDate(from).getTime(); t <= end; t += DAY_MS) out.push(new Date(t).toISOString().slice(0, 10))
  return out
}

/**
 * Build a mirror from the day records the availability stream stores.
 *
 * `days` is a flat array of { listingId, date, status, blocks, minNights, maxNights, allotment,
 * cta, ctd, ... } — exactly the projection `connector-guesty`'s availability stream yields, so the
 * mirror consumes what the connector produces with nothing in between reshaping it.
 */
export function buildMirror({ days = [], windowStart, windowEnd, lastChangeAt = {} } = {}) {
  if (!windowStart || !windowEnd) {
    throw new TypeError('a mirror without a window cannot answer anything honestly — pass windowStart and windowEnd')
  }
  parseDate(windowStart)
  parseDate(windowEnd)

  const byKey = new Map()
  for (const d of days) {
    if (!d || typeof d.listingId !== 'string' || typeof d.date !== 'string') continue
    byKey.set(`${d.listingId}:${d.date.slice(0, 10)}`, d)
  }

  return {
    windowStart,
    windowEnd,
    size: byKey.size,

    /** Is this date one the mirror could possibly know about? */
    covers: (date) => date >= windowStart && date <= windowEnd,

    /**
     * Returns { day } | { outsideWindow: true } | { missing: true }.
     *
     * Three outcomes, never two. The caller is forced to distinguish "we hold no data that far
     * out" from "we should hold this day and do not" — the second is a sync fault and deserves to
     * be noticed rather than absorbed.
     */
    lookup(listingId, date) {
      if (date < windowStart || date > windowEnd) return { outsideWindow: true }
      const day = byKey.get(`${listingId}:${date}`)
      return day ? { day } : { missing: true }
    },

    /**
     * When this listing's data last changed. Per listing, never global: one stale unit must not be
     * disguised by a hundred and twenty-four fresh ones.
     */
    lastChangeFor: (listingId) => lastChangeAt[listingId] ?? null,
  }
}
